/**
 * Suggestion row — the VISUAL content of one autocomplete option.
 *
 * Option semantics (role=option, id, aria-selected, highlight, click/keyboard)
 * are owned by `<ComboboxOption>` (the `@letsgethome/ui/combobox` primitive);
 * this renders only the icon + title/subtitle/count. `suggestionHref` is the
 * frontend route builder (backend returns slug + type; the frontend owns
 * routing), consumed by the SearchBar's per-option `onSelect`.
 *
 * A district/estate row's LINK and its COUNT LABEL are both derived from one
 * `suggestionDestination` call (LET-89), so the label names the mode the link
 * goes to — "1 for sale" — and can never promise a hub that shows nothing.
 */
import { useLocale, useTranslations } from '@/shared/i18n/useTranslations';
import { buildEstateSlugSegment, buildEstateUrl } from '@/shared/lib/estates/url';
import { buildListingUrl } from '@/shared/lib/listings/url';
import type { SuggestionItemData, SuggestionItemVariants } from '@/shared/lib/types/suggestions';
import { projectLocalized } from '@letsgethome/lib/i18n';
import type { SuggestionTypeValue } from '@letsgethome/lib/types/listings';
import { routes, type ListingsMode, type Route } from '@letsgethome/routes';
import {
  plaintextString,
  slug as toSlug,
  type Locale,
  type LocalizedText,
} from '@letsgethome/types/branded';
import { Building2, Home, KeyRound, MapPin } from 'lucide-react';

import {
  suggestionDestination,
  type HubSuggestion,
  type SuggestionDestination,
} from './suggestion-destination';

/**
 * The destination of a district / estate suggestion — the hub of a mode that
 * HAS listings (the current browse mode first), else the entity's own page.
 *
 * Keyed by the two hub-navigating variants so each builder receives the
 * NARROWED member and its BRANDED id: handing a district's id to
 * `routes.listings.browse(..., { estateId })` is a compile error, cast-free.
 * The fall-through pages are real content pages that never say "0 listings":
 * an estate's detail page, and the estates index filtered to the district
 * (a neighbourhood row has no page of its own — `/districts/:slug` is the
 * 18 official districts, `manifests/districts.yaml`).
 */
const HUB_DESTINATIONS: {
  [K in HubSuggestion['type']]: (
    item: SuggestionItemVariants[K],
    wireSlug: string,
    mode: ListingsMode
  ) => SuggestionDestination;
} = {
  district: (item, _slug, mode) =>
    suggestionDestination(item, mode, {
      hub: (m) => routes.listings.browse(m, { districtId: item.id }),
      entity: routes.estates.index({ districtId: item.id }),
    }),
  estate: (item, wireSlug, mode) =>
    suggestionDestination(item, mode, {
      hub: (m) => routes.listings.browse(m, { estateId: item.id }),
      entity: buildEstateUrl(toSlug(wireSlug), item.publicId),
    }),
};

/**
 * Frontend URL builders keyed by `storefront.v1.SuggestionType` variant.
 * Backend returns slug + type; the frontend owns routing.
 *
 * `SuggestionType` has no `wire_aliases_gen` entry — the lowercase short name
 * IS its canonical wire form (matches `PROTO_ENUM_VALUE_SETS.SuggestionType`).
 * Keying the mapped type over `SuggestionTypeValue` makes this exhaustive by
 * construction (a new variant fails to compile until handled) and routes the
 * variant literals through object keys, which `no-raw-wire-alias-string` does
 * not flag — so no per-site opt-out is needed. Mirrors `ICON_MAP` below.
 *
 * Each builder receives the NARROWED union member
 * (`SuggestionItemVariants[K]`), so `item.id` inside a lambda carries the
 * variant's BRANDED id (the correlated-record dispatch of
 * microsoft/TypeScript#47109).
 */
const HREF_BUILDERS: {
  [K in SuggestionTypeValue]: (
    item: SuggestionItemVariants[K],
    wireSlug: string,
    wireParentSlug: string,
    mode: ListingsMode
  ) => Route;
} = {
  // District & estate go through the DERIVED destination (LET-89): the
  // filtered listings hub — keyed by the backend's own filter id
  // (`SearchFilters.district_id` / `estate_id`), so no name→id resolution and
  // no drift — for a mode that actually has listings, else the entity page.
  district: (item, wireSlug, _parent, mode) => HUB_DESTINATIONS.district(item, wireSlug, mode).href,
  estate: (item, wireSlug, _parent, mode) => HUB_DESTINATIONS.estate(item, wireSlug, mode).href,
  // A listing suggestion is an exact match on a public id, so it goes straight
  // to the listing (S-4) — there is nothing to filter down to. The URL is the
  // shared `{slug}-{publicId}` composition, not a bespoke one.
  listing: (item, wireSlug) => buildListingUrl(toSlug(wireSlug), item.publicId),
  // Buildings can't be a SearchFilters key (no `building_id`), so they navigate
  // to the parent estate with a building anchor.
  building: (item, wireSlug, wireParentSlug) =>
    wireParentSlug && item.publicId
      ? routes.estates.building(
          toSlug(buildEstateSlugSegment(toSlug(wireParentSlug), item.publicId)),
          toSlug(wireSlug)
        )
      : routes.estates.detail(toSlug(wireSlug)),
};

/**
 * Correlated dispatch into {@link HREF_BUILDERS}: because both the builders map
 * and this signature are indexed by the SAME `SuggestionItemVariants[K]`, TS
 * proves `HREF_BUILDERS[item.type]` accepts exactly this `item` — no cast, no
 * union-signature intersection problem. The lookup is a known-key access
 * (total + always defined); exhaustiveness stays compile-time.
 */
function buildHref<K extends SuggestionTypeValue>(
  item: SuggestionItemVariants[K] & { type: K },
  wireSlug: string,
  wireParentSlug: string,
  mode: ListingsMode
): Route {
  return HREF_BUILDERS[item.type](item, wireSlug, wireParentSlug, mode);
}

/** Build the frontend URL for a suggestion. `mode` is the browse mode the user
 *  is on — the PREFERRED hub for a district/estate selection; it defaults to
 *  `rent` (the home search's default + the storefront default mode). */
export function suggestionHref(
  item: SuggestionItemData,
  locale: Locale,
  mode: ListingsMode = 'rent'
): Route {
  const slug = projectLocalized(item.localizedSlug, locale);
  const parentSlug = projectLocalized(item.parentSlug, locale);
  return buildHref(item, slug, parentSlug, mode);
}

type SearchT = ReturnType<typeof useTranslations<'search'>>;

/** The count label of a hub destination: its count, naming its mode. */
function hubCountLabel(destination: SuggestionDestination, t: SearchT): LocalizedText | undefined {
  if (destination.kind !== 'hub') return undefined;
  // `destination.mode` is total over `ListingsMode`; the key names the mode.
  return destination.mode === 'rent'
    ? t('rentCount', { count: destination.count })
    : t('saleCount', { count: destination.count });
}

/**
 * The count label a row shows, if any, keyed by variant like
 * {@link HREF_BUILDERS} (exhaustive, no wire-literal comparison): for the two
 * hub-navigating variants it is the count of the mode the link goes to,
 * derived from the SAME destination as the href, so the two cannot disagree.
 * Building/listing rows carry no counts and show none.
 */
const COUNT_LABELS: {
  [K in SuggestionTypeValue]: (
    item: SuggestionItemVariants[K],
    wireSlug: string,
    mode: ListingsMode,
    t: SearchT
  ) => LocalizedText | undefined;
} = {
  district: (item, wireSlug, mode, t) =>
    hubCountLabel(HUB_DESTINATIONS.district(item, wireSlug, mode), t),
  estate: (item, wireSlug, mode, t) =>
    hubCountLabel(HUB_DESTINATIONS.estate(item, wireSlug, mode), t),
  building: () => undefined,
  listing: () => undefined,
};

/** Correlated dispatch into {@link COUNT_LABELS} — see {@link buildHref}. */
function suggestionCountLabel<K extends SuggestionTypeValue>(
  item: SuggestionItemVariants[K] & { type: K },
  locale: Locale,
  mode: ListingsMode,
  t: SearchT
): LocalizedText | undefined {
  const slug = projectLocalized(item.localizedSlug, locale);
  return COUNT_LABELS[item.type](item, slug, mode, t);
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  district: MapPin,
  estate: Home,
  building: Building2,
  listing: KeyRound,
};

/** The visual content of one suggestion (no option semantics — see module doc). */
export function SuggestionRow({
  item,
  mode = 'rent',
}: {
  item: SuggestionItemData;
  /** The browse mode the row's link prefers — see {@link suggestionHref}. */
  mode?: ListingsMode;
}) {
  const t = useTranslations('search');
  const locale = useLocale();
  const Icon = ICON_MAP[item.type] ?? MapPin;

  const title = projectLocalized(item.title, locale);
  const subtitle = projectLocalized(item.subtitle, locale);
  const countText = suggestionCountLabel(item, locale, mode, t);

  return (
    <>
      <Icon className="size-5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-foreground">{plaintextString(title)}</div>
        {subtitle && (
          <div className="truncate text-sm text-muted-foreground">{plaintextString(subtitle)}</div>
        )}
      </div>
      {countText && <div className="shrink-0 text-sm text-muted-foreground">{countText}</div>}
    </>
  );
}
